import { FocusOptions } from '@angular/cdk/a11y';
import { TmFormElement } from '@thiememeulenhoff/lit-sdk';
import { IconName } from '@thiememeulenhoff/types';
import { html, nothing } from 'lit';
import {
  customElement,
  property,
  query,
  queryAssignedNodes,
  state,
} from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styles, tag } from './text-input.styles';

@customElement(tag)
export class TextInputComponent extends TmFormElement {
  static override styles = styles;

  @property({ type: String })
  override type:
    | 'date'
    | 'datetime-local'
    | 'text'
    | 'email'
    | 'number'
    | 'password'
    | 'tel'
    | 'url' = 'text';

  @property({ type: String, reflect: true })
  icon?: IconName;

  @property({ type: Boolean, reflect: true })
  valid? = true;

  @property({ type: Boolean, reflect: true })
  validate? = true;

  @property({ type: Number, reflect: true })
  minlength?: number;

  @property({ type: Number, reflect: true })
  maxlength?: number;

  @property({ type: String, reflect: true })
  size?: string;

  @property({ type: Boolean, reflect: true })
  clearable = false;

  @property({ type: Boolean, reflect: true })
  pill = false;

  @query('#inputElementWrapper')
  wrapper?: HTMLElement;

  @query('#inputElement')
  inputElement?: HTMLElement;

  @state()
  hasLabel = false;

  @queryAssignedNodes()
  defaultSlotNodes!: Array<Node>;

  inputTimeout?: number;

  override inputTemplate() {
    return html` <input
      id="inputElement"
      part="input"
      data-test="input"
      role="textbox"
      name=${this.name ?? nothing}
      type=${this.type}
      placeholder=${this.placeholder ?? nothing}
      ?disabled=${this.disabled}
      ?required=${this.required}
      ?readonly=${this.readonly}
      minlength=${this.minlength ?? nothing}
      maxlength=${this.maxlength ?? nothing}
      .checked=${this.checked}
      .value=${this.value}
      @input=${this.handleInput}
      @change=${this.handleChange}
      class="${classMap({
        error: !this.valid,
        [`size-${this.size}`]: this.size !== undefined,
        pill: this.pill,
      })}"
    />`;
  }

  override handleInput(event: InputEvent) {
    const input = event.target as HTMLInputElement;

    if (this.inputTimeout) {
      clearTimeout(this.inputTimeout);
    }

    this.inputTimeout = window.setTimeout(() => {
      if (!this.validate) {
        this.valid = input.checkValidity();
      }
    }, 500);

    super.handleInput(event);
  }

  override async focus(options?: FocusOptions) {
    await this.updateComplete;
    if (this.inputElement) {
      this.inputElement.focus(options);
    }
  }

  override handleChange(event: Event) {
    super.handleChange(event);
    this.#updateHasContent();
  }

  #handleSlotchange() {
    this.hasLabel = (this.defaultSlotNodes?.length || 0) > 0;
  }

  protected override firstUpdated() {
    this.#handleSlotchange();
  }

  #clearTemplate() {
    return this.clearable
      ? html`<a-icon
          data-test="reset-filter-search"
          class="icon embedded clickable reset small"
          name="close"
          @mousedown=${(e: MouseEvent) => e.preventDefault()}
          @click=${this.#handleClear}
        >
        </a-icon>`
      : nothing;
  }

  #iconTemplate() {
    return this.icon
      ? html`<a-icon
          class="icon embedded clickable"
          data-test="input-icon"
          name=${this.icon}
          part="icon"
          @mousedown=${(e: MouseEvent) => e.preventDefault()}
          @click=${this.#handleIconClick}
        >
        </a-icon>`
      : nothing;
  }

  #renderError() {
    return !this.valid ? html`<slot name="error"></slot>` : nothing;
  }

  override render() {
    return html`
      <label
        for="inputElement"
        class="${classMap({
          error: !this.valid,
          show: this.hasLabel,
        })}"
      >
        <slot @slotchange=${this.#handleSlotchange}></slot>
      </label>
      <slot name="description"></slot>
      <div
        id="inputElementWrapper"
        class="${classMap({
          'input-wrapper': true,
          'with-icon': this.icon !== undefined || this.clearable,
          'with-icon-and-reset': this.icon !== undefined && this.clearable,
          'has-content': this.value ? this.value.length > 0 : false,
        })}"
      >
        ${this.inputTemplate()} ${this.#clearTemplate()} ${this.#iconTemplate()}
      </div>
      ${this.#renderError()}
    `;
  }

  clear() {
    this.value = '';
    this.#updateHasContent();
  }

  #handleClear(event: Event) {
    event.stopPropagation();
    this.clear();
    this.dispatchLocalEvent({ eventName: 'clear' });
    this.dispatchEvent(new Event('change', event));
  }

  #handleIconClick() {
    this.dispatchLocalEvent({ eventName: 'icon-click' });
  }

  #updateHasContent() {
    if (this.wrapper) {
      this.wrapper.classList.toggle('has-content', this.value.length > 0);
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'a-text-input': TextInputComponent;
  }
}
